import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { Toast } from '../../components/base/Toast';
import { api } from '../../services/api';
import { FloatingActionButton } from '../../components/base/FloatingActionButton';
import PermissionsModal from '../../components/feature/PermissionsModal';
import { roleLabels, roleIcons } from '../../mocks/stocks';
import type { RoleUtilisateur } from '../../mocks/commandes';
import { checkPermission, PERMISSIONS } from '../../utils/permissions';

interface UserInfo { id: string; nom: string; role: string; }

interface Utilisateur {
  id: string;
  nom: string;
  role: RoleUtilisateur;
  email: string;
  pin?: string;
  avatar: string;
  actif: boolean;
  permissions?: string[];
}

// Les tailleurs et coupeurs sont désormais gérés dans "Personnel"
const ROLES: RoleUtilisateur[] = ['gerant', 'vendeur', 'responsable_atelier'];

export default function GestionUtilisateurs() {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [utilisateurs, setUtilisateurs] = useState<Utilisateur[]>([]);
  const [trashUsers, setTrashUsers] = useState<Utilisateur[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTrash, setShowTrash] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [editModal, setEditModal] = useState<Utilisateur | null>(null);
  const [addModal, setAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState<RoleUtilisateur | 'tous'>('tous');
  const [newUser, setNewUser] = useState({ nom: '', role: 'vendeur' as RoleUtilisateur, pin: '', email: '' });
  const [permissionModal, setPermissionModal] = useState<Utilisateur | null>(null);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    setLoading(true);
    try {
      const data = await api.getUsers(token);
      setUtilisateurs(data.filter((u: any) => ROLES.includes(u.role as RoleUtilisateur) || u.role === 'superadmin'));
    } catch (err) {
      setToast({ message: "Erreur de chargement", type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTrash = async () => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      const data = await api.getTrashUsers(token);
      setTrashUsers(data);
    } catch (err) {
      setToast({ message: "Erreur corbeille", type: 'error' });
    }
  };

  useEffect(() => {
    const stored = localStorage.getItem('atelier_bassoum_user');
    const token = localStorage.getItem('atelier_bassoum_token');
    if (stored && token) {
      setUser(JSON.parse(stored));
      fetchData();
    }
    else navigate('/login');
  }, [navigate, fetchData]);

  const showMsg = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const filtered = utilisateurs.filter(u => {
    const q = searchQuery.toLowerCase();
    const matchQ = !q || u.nom.toLowerCase().includes(q) || (u.email && u.email.toLowerCase().includes(q));
    const matchRole = filterRole === 'tous' || u.role === filterRole;
    return matchQ && matchRole;
  });

  const handleUpdateUser = async () => {
    if (!editModal) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      const updateData: any = { role: editModal.role, email: editModal.email };
      if (editModal.pin && editModal.pin !== '****' && editModal.pin.trim() !== '') {
        updateData.pin = editModal.pin;
      }
      await api.updateUser(token, editModal.id, updateData);
      showMsg('Utilisateur mis à jour');
      setEditModal(null);
      fetchData();
    } catch (err) {
      showMsg('Erreur mise à jour', 'error');
    }
  };

  const handleToggleActif = async (u: Utilisateur) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.updateUser(token, u.id, { actif: u.actif ? 0 : 1 });
      showMsg(`${u.nom} ${u.actif ? 'désactivé' : 'activé'}`);
      fetchData();
    } catch (err) {
      showMsg('Erreur action', 'error');
    }
  };

  const handleDeleteUser = async (id: string) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.deleteUser(token, id);
      showMsg('Utilisateur mis à la corbeille');
      fetchData();
    } catch (err) {
      showMsg('Erreur suppression', 'error');
    }
  };

  const handleRestoreUser = async (id: string) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.restoreUser(token, id);
      showMsg('Utilisateur restauré');
      fetchTrash();
      fetchData();
    } catch (err) {
      showMsg('Erreur restauration', 'error');
    }
  };

  const handleHardDeleteUser = async (id: string) => {
    if (!confirm('Supprimer définitivement cet utilisateur ?')) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.hardDeleteUser(token, id);
      showMsg('Utilisateur supprimé définitivement');
      fetchTrash();
    } catch (err) {
      showMsg('Erreur suppression', 'error');
    }
  };

  const handleAddUser = async () => {
    if (!newUser.nom.trim() || !newUser.pin.trim()) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.createUser(token, {
        nom: newUser.nom,
        role: newUser.role,
        email: newUser.email || '', // Allow empty email as it's optional
        pin: newUser.pin,
        actif: 1
      });
      showMsg(`${newUser.nom} ajouté`);
      setAddModal(false);
      setNewUser({ nom: '', role: 'vendeur', pin: '', email: '' });
      fetchData();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Erreur lors de la création';
      showMsg(msg, 'error');
    }
  };

  const handleOpenPermissions = (u: Utilisateur) => {
    setPermissionModal(u);
  };

  const roleColors: Record<string, string> = {
    superadmin: 'bg-amber-100 text-amber-700',
    gerant: 'bg-foret-100 text-foret-700',
    vendeur: 'bg-terre-100 text-terre-700',
    responsable_atelier: 'bg-or-100 text-or-700',
  };

  if (!user) return null;
  
  // Security: check for explicit permission
  if (!checkPermission(PERMISSIONS.USER_GERER)) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50 p-10 text-center">
        <div className="w-20 h-20 bg-red-50 text-red-500 rounded-3xl flex items-center justify-center mb-6 shadow-sm">
          <i className="ri-shield-user-line text-4xl" />
        </div>
        <h2 className="font-playfair font-black text-3xl text-gray-900 mb-2">Accès restreint</h2>
        <p className="text-gray-400 max-w-md mx-auto font-medium">Désolé, seul l'Administrateur principal est autorisé à gérer les comptes et les permissions de l'atelier.</p>
        <button 
          onClick={() => navigate('/dashboard')}
          className="mt-8 px-8 py-3 bg-gray-900 text-white rounded-2xl font-black text-sm hover:bg-black transition-all shadow-xl active:scale-95"
        >
          RETOUR AU TABLEAU DE BORD
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="hidden md:block">
        <Sidebar role={user.role} nom={user.nom} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-playfair font-bold text-xl text-gray-900">Gestion des utilisateurs</h2>
            <p className="text-xs text-gray-400 mt-0.5">{utilisateurs.length} membres · {utilisateurs.filter(u => u.actif).length} actifs</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setShowTrash(true); fetchTrash(); }}
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-600 px-3 py-2 rounded-xl text-sm font-semibold cursor-pointer transition-colors"
              title="Corbeille"
            >
              <i className="ri-delete-bin-line" />
              <span className="hidden sm:inline">Corbeille</span>
            </button>
            <button
              onClick={() => setAddModal(true)}
              className="hidden md:flex items-center gap-2 bg-foret-500 hover:bg-foret-600 text-white px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-user-add-line" />
              Ajouter
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 opacity-40">
              <div className="w-10 h-10 border-4 border-foret-500 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-sm font-medium">Chargement des utilisateurs...</p>
            </div>
          ) : (
            <>
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="relative flex-1 max-w-sm">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                    <i className="ri-search-line" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Rechercher un employé…"
                    className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400"
                  />
                </div>
                <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1 overflow-x-auto">
                  <button
                    onClick={() => setFilterRole('tous')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      filterRole === 'tous' ? 'bg-foret-500 text-white' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    Tous
                  </button>
                  {ROLES.map(r => (
                    <button
                      key={r}
                      onClick={() => setFilterRole(r)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                        filterRole === r ? 'bg-foret-500 text-white' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {roleLabels[r]}
                    </button>
                  ))}
                </div>
              </div>

              {/* User cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filtered.map(u => (
                  <div
                    key={u.id}
                    className={`bg-white rounded-2xl border p-5 transition-all shadow-sm flex flex-col ${u.actif ? 'border-gray-100' : 'border-gray-200 opacity-60 bg-gray-50/50'}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${
                          u.actif ? 'bg-foret-400 shadow-lg shadow-foret-100' : 'bg-gray-300'
                        }`}>
                          {u.avatar || u.nom[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900 leading-tight">{u.nom}</p>
                          <span className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${roleColors[u.role] || 'bg-gray-100 text-gray-600'}`}>
                            {roleLabels[u.role] || u.role}
                          </span>
                        </div>
                      </div>
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${u.actif ? 'bg-green-400 ring-4 ring-green-50' : 'bg-gray-300'}`} />
                    </div>

                    <div className="space-y-2 mb-5 flex-1">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <i className="ri-mail-line opacity-50" />
                        <span className="truncate">{u.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <i className="ri-lock-password-line opacity-50" />
                        <span>PIN : ****</span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                       <button
                        onClick={() => setEditModal(u)}
                        className="p-2 rounded-xl border border-gray-100 text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors"
                        title="Modifier le rôle"
                      >
                        <i className="ri-edit-2-line" />
                      </button>

                      {u.role !== 'superadmin' && (
                        <button
                          onClick={() => handleOpenPermissions(u)}
                          className="p-2 rounded-xl border border-gray-100 text-foret-600 hover:bg-foret-50 cursor-pointer transition-colors"
                          title="Gérer les permissions"
                        >
                          <i className="ri-shield-keyhole-line" />
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleToggleActif(u)}
                        className={`flex-1 text-[11px] py-2 rounded-xl border font-bold transition-all cursor-pointer ${
                          u.actif
                            ? 'border-red-100 text-red-500 hover:bg-red-50'
                            : 'border-green-100 text-green-600 hover:bg-green-50'
                        }`}
                      >
                        {u.actif ? 'Désactiver' : 'Activer'}
                      </button>

                      {u.role !== 'superadmin' && (
                        <button
                          onClick={() => handleDeleteUser(u.id)}
                          className="p-2 rounded-xl bg-red-50 text-red-500 hover:bg-red-500 hover:text-white cursor-pointer transition-all border border-red-100"
                          title="Supprimer"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </main>
      </div>

      {/* Edit Modal */}
      {editModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 animate-in fade-in" onClick={() => setEditModal(null)}>
          <div className="bg-white rounded-[2rem] p-8 w-full max-w-sm shadow-2xl animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <h3 className="font-playfair font-black text-2xl text-gray-900 mb-1 tracking-tight">Modifier</h3>
            <p className="text-sm font-medium text-gray-400 mb-6">{editModal.nom}</p>
            
            <div className="space-y-4 mb-8">
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 px-1">Email</label>
                <input
                  type="email"
                  id="edit-email"
                  title="Modifier l'email"
                  placeholder="nom@atelier.ml"
                  value={editModal.email || ''}
                  onChange={e => setEditModal({ ...editModal, email: e.target.value })}
                  className="w-full border-2 border-gray-50 bg-gray-50/30 rounded-2xl px-4 py-3 text-sm font-medium focus:outline-none focus:border-foret-400 focus:bg-white transition-all"
                />
              </div>
              
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 px-1">Rôle</label>
                <select
                  value={editModal.role}
                  title="Modifier le rôle"
                  onChange={e => setEditModal({ ...editModal, role: e.target.value as RoleUtilisateur })}
                  className="w-full border-2 border-gray-50 bg-gray-50/30 rounded-2xl px-4 py-3 text-sm font-bold focus:outline-none focus:border-foret-400 focus:bg-white appearance-none cursor-pointer"
                >
                  {ROLES.map(r => (
                    <option key={r} value={r}>{roleLabels[r]}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 px-1">Nouveau PIN (Optionnel)</label>
                <input
                  type="password"
                  value={editModal.pin === '****' ? '' : editModal.pin}
                  onChange={e => setEditModal({ ...editModal, pin: e.target.value })}
                  placeholder="Laisser vide pour ne pas changer"
                  maxLength={4}
                  className="w-full border-2 border-gray-50 bg-gray-50/30 rounded-2xl px-4 py-3 text-sm font-medium tracking-[0.5em] focus:outline-none focus:border-foret-400 focus:bg-white transition-all"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setEditModal(null)}
                className="flex-1 py-3.5 rounded-2xl border border-gray-100 text-gray-500 font-bold text-sm cursor-pointer hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleUpdateUser}
                className="flex-1 py-3.5 rounded-2xl bg-foret-500 text-white font-bold text-sm cursor-pointer hover:bg-foret-600 shadow-lg shadow-foret-200 transition-all active:scale-95"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {addModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 animate-in fade-in" onClick={() => setAddModal(false)}>
          <div className="bg-white rounded-[2rem] p-8 w-full max-w-sm shadow-2xl animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <h3 className="font-playfair font-black text-2xl text-gray-900 mb-6 tracking-tight">Nouveau compte</h3>
            <div className="space-y-5 mb-8">
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 px-1">Nom complet</label>
                <input
                  type="text"
                  value={newUser.nom}
                  onChange={e => setNewUser(prev => ({ ...prev, nom: e.target.value }))}
                  placeholder="ex: Mamadou Koné"
                  className="w-full border-2 border-gray-50 bg-gray-50/30 rounded-2xl px-4 py-3 text-sm font-medium focus:outline-none focus:border-foret-400 focus:bg-white transition-all"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 px-1">Rôle</label>
                <select
                  value={newUser.role}
                  title="Choisir le rôle de l'utilisateur"
                  onChange={e => setNewUser(prev => ({ ...prev, role: e.target.value as RoleUtilisateur }))}
                  className="w-full border-2 border-gray-50 bg-gray-50/30 rounded-2xl px-4 py-3 text-sm font-bold focus:outline-none focus:border-foret-400 focus:bg-white appearance-none cursor-pointer"
                >
                  {ROLES.map(r => (
                    <option key={r} value={r}>{roleLabels[r]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 px-1">Email (optionnel)</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={e => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="nom@atelier.ml"
                  className="w-full border-2 border-gray-50 bg-gray-50/30 rounded-2xl px-4 py-3 text-sm font-medium focus:outline-none focus:border-foret-400 focus:bg-white transition-all"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 px-1">Code PIN (4 chiffres)</label>
                <input
                  type="password"
                  value={newUser.pin}
                  onChange={e => setNewUser(prev => ({ ...prev, pin: e.target.value }))}
                  placeholder="••••"
                  maxLength={4}
                  className="w-full border-2 border-gray-50 bg-gray-50/30 rounded-2xl px-4 py-3 text-sm font-medium tracking-[0.5em] focus:outline-none focus:border-foret-400 focus:bg-white transition-all"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setAddModal(false)}
                className="flex-1 py-3.5 rounded-2xl border border-gray-100 text-gray-500 font-bold text-sm cursor-pointer hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleAddUser}
                className="flex-1 py-3.5 rounded-2xl bg-foret-500 text-white font-bold text-sm cursor-pointer hover:bg-foret-600 shadow-lg shadow-foret-200 transition-all active:scale-95"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trash Modal */}
      {showTrash && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in" onClick={() => setShowTrash(false)}>
          <div className="bg-white rounded-[2.5rem] p-10 w-full max-w-2xl shadow-2xl flex flex-col max-h-[85vh] transform animate-in slide-in-from-bottom-10" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-8 flex-shrink-0">
              <div className="flex items-center gap-5">
                <div className="w-16 h-16 bg-red-50 rounded-3xl flex items-center justify-center">
                    <i className="ri-delete-bin-7-line text-3xl text-red-500" />
                </div>
                <div>
                    <h3 className="font-playfair font-black text-3xl text-gray-900 tracking-tight">Corbeille</h3>
                    <p className="text-gray-400 font-medium mt-1">Utilisateurs supprimés</p>
                </div>
              </div>
              <button 
                onClick={() => setShowTrash(false)} 
                title="Fermer la corbeille"
                className="w-12 h-12 flex items-center justify-center rounded-2xl hover:bg-gray-100 text-gray-400 cursor-pointer transition-all active:scale-95"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-1 space-y-4 pr-2">
              {trashUsers.length === 0 ? (
                <div className="text-center py-20 opacity-30">
                  <i className="ri-inbox-line text-5xl mb-4 block" />
                  <p className="font-bold">La corbeille est vide</p>
                </div>
              ) : (
                trashUsers.map(u => (
                  <div key={u.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-3xl group border border-transparent hover:border-gray-200 transition-all">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-gray-200 flex items-center justify-center text-gray-500 font-bold">
                        {u.avatar || u.nom[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">{u.nom}</p>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{roleLabels[u.role] || u.role}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleRestoreUser(u.id)}
                        className="w-10 h-10 flex items-center justify-center rounded-2xl bg-white text-green-600 hover:bg-green-500 hover:text-white transition-all cursor-pointer shadow-sm"
                        title="Restaurer"
                      >
                        <i className="ri-restart-line text-lg" />
                      </button>
                      <button
                        onClick={() => handleHardDeleteUser(u.id)}
                        className="w-10 h-10 flex items-center justify-center rounded-2xl bg-white text-red-50 text-red-500 hover:bg-red-500 hover:text-white transition-all cursor-pointer shadow-sm"
                        title="Supprimer définitivement"
                      >
                        <i className="ri-delete-bin-2-line text-lg" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {permissionModal && (
        <PermissionsModal 
          user={permissionModal} 
          onClose={() => setPermissionModal(null)} 
          onSuccess={() => showMsg('Permissions mises à jour avec succès')}
        />
      )}
      <FloatingActionButton 
        onClick={() => setAddModal(true)} 
        icon="ri-user-add-line" 
        label="Ajouter un utilisateur" 
      />
    </div>
  );
}

